import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckSquare } from 'lucide-react';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import TrafficLightDot from '../../../shared/TrafficLightDot';
import { CALENDAR_ITEM_COLORS } from '../../dashboard/calendarConstants';
import { thumbFileUrl } from '../../../shared/mediaFileUrl';
import { PlaceholderThumb, PersonPlaceholderThumb } from '../../../shared/ProductThumb';
import CommercialTermsPrompt, { hasCommercialTerms } from './CommercialTermsPrompt';
import ConvertToCustomerDialog from './ConvertToCustomerDialog';
import CloseProspectDialog from './CloseProspectDialog';
import ReopenProspectDialog from './ReopenProspectDialog';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';

const columnStyle = {
  flex: '1 1 0',
  minWidth: 220,
  background: '#f0f2f5',
  borderRadius: 10,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  maxHeight: 'calc(100vh - 220px)',
};

const cardStyle = {
  position: 'relative',
  background: '#fff',
  border: '1px solid #d7dbe3',
  borderRadius: 8,
  // Right padding clears the card's ⋮ menu (and open-Task icon).
  padding: '10px 56px 10px 12px',
  cursor: 'grab',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

// Pipeline board — the Prospect Relationship Type's open Stages as Kanban
// columns (Lead/Qualified/Opportunity plus any open Custom Stage — see
// RelationshipTypeTable.js). Closed Stages (Disqualified, Lost) and
// Customers stay off it (vis_relationship_lifecycle.md §6), bar the Revisit
// column after the open Stages: closed Prospects whose revisit date is
// coming up or due (issue #246) — this board is
// an all-at-once view onto the still-in-motion Prospects on Prospects &
// Customers, not a separate concept from it. Each card's status dot (see TrafficLightDot.js) flags
// whether it needs attention, driven by its most recently logged Activity/
// Task Outcome (pipelineDotResolver.js).
// Every open Prospect, page by page — the list endpoint caps a page at 100,
// and the board has to show them all, not just the first page.
// pipeline_dot resolves each card's status dot server-side (story #227) —
// opted in here rather than on every consumer of this list, since it walks
// each entity's own Activity rollup.
// The Revisit column's cards come the same way with { revisit: true }
// instead (issue #246).
async function fetchAllCards(params) {
  const cards = [];
  for (let page = 1; ; page += 1) {
    const res = await axios.get('/api/commercial-entities', { params: { status: 'active', limit: 100, page, ...params } });
    cards.push(...(res.data.data || []));
    if (page >= (res.data.pagination?.pages || 1)) return cards;
  }
}

function CustomerPipelinePage() {
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [stages, setStages] = useState([]);
  const [entities, setEntities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  const [dragOverCardId, setDragOverCardId] = useState(null);
  // A drop into a Stage requiring commercial terms by a card without them:
  // { entity, stage, send(terms) } — held until the Tier prompt confirms
  // (then sent with the terms) or cancels (card stays where it was).
  const [pendingMove, setPendingMove] = useState(null);
  // The card whose "Won — Convert to Customer" dialog is open — once
  // converted it's a Customer, so it drops off the board on refetch.
  const [convertingEntity, setConvertingEntity] = useState(null);
  // Close / Reopen dialogs (issue #246) — either way the card changes
  // column or leaves the board, so the board refetches.
  const [closingEntity, setClosingEntity] = useState(null);
  const [reopeningEntity, setReopeningEntity] = useState(null);
  // Closed Prospects whose revisit is coming up or due — the Revisit column
  // after the open Stages. Their cards can't be dragged; Reopen or Close
  // moves them on.
  const [revisitEntities, setRevisitEntities] = useState([]);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, cards, revisitCards] = await Promise.all([
        axios.get('/api/relationship-types', { params: { status: 'active' } }),
        fetchAllCards({ pipeline_dot: true }),
        fetchAllCards({ revisit: true }),
      ]);
      setRevisitEntities(revisitCards);
      const prospectType = (typesRes.data.data || []).find((t) => t.system_key === 'prospect');
      // Stages already come back ordered by sort_order.
      setStages((prospectType?.stages || []).filter((s) => s.is_open && s.is_active));
      // /api/commercial-entities only ever returns Prospects at an open
      // Stage — exactly this board's cards.
      setEntities(cards);
    } catch (err) {
      showError(err, 'Could not load pipeline');
    }
    setLoading(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const prospectRelationship = (entity) => (entity.relationships || []).find((r) => r.relationship_type?.system_key === 'prospect');

  // The one linked person worth showing on a card too small for a full
  // contact list — lowest (most important) Link Role priority among this
  // entity's current, individual-side links; oldest link breaks a tie
  // between two contacts holding the same role. Links to other
  // organisations, and ended links, never count.
  const mostImportantContact = (entity) => (entity.links || [])
    .filter((l) => !l.ended_at && l.related_entity?.type === 'individual')
    .sort((a, b) => {
      const pa = a.link_role?.priority ?? Infinity;
      const pb = b.link_role?.priority ?? Infinity;
      return (pa - pb) || (new Date(a.createdAt) - new Date(b.createdAt));
    })[0]?.related_entity;

  // Ordered by the relationship's own drag position; id as a tiebreak so
  // untouched rows (sequence 0 default, pre-reorder) still render stably.
  // The name filter narrows which cards render at all, without touching
  // stage membership or drag/reorder, which still act on the full board.
  const matchesSearch = (e) => (e.name || '').toLowerCase().includes(search.trim().toLowerCase());
  const cardsForStage = (stageId) => entities
    .filter((e) => prospectRelationship(e)?.relationship_stage_id === stageId)
    .filter(matchesSearch)
    .sort((a, b) => (prospectRelationship(a).sequence - prospectRelationship(b).sequence) || (a.id - b.id));
  // Soonest revisit first.
  const revisitCards = revisitEntities
    .filter(matchesSearch)
    .sort((a, b) => (a.revisit?.date || '').localeCompare(b.revisit?.date || '') || (a.id - b.id));

  // A Revisit card's dot: Yellow while the revisit is coming up, Green once
  // it's due or overdue.
  const revisitDot = (entity) => {
    const due = entity.revisit?.date && entity.revisit.date <= todayInTenant();
    const reason = prospectRelationship(entity)?.close_reason?.name;
    return {
      state: due ? 'green' : 'yellow',
      title: due ? 'Revisit due' : 'Revisit coming up',
      detail: `${reason ? `${reason} — ` : ''}revisit ${due ? 'was due' : 'due'} ${formatDate(entity.revisit?.date)}`,
    };
  };

  // Runs a Stage move now, or first asks for the Tier (and Customer Price
  // Group) when the target Stage requires commercial terms the record
  // doesn't have yet. send(terms) makes the request; terms is {} when no
  // prompt was needed.
  const withCommercialTerms = (entity, stageId, send) => {
    const stage = stages.find((s) => s.id === stageId);
    if (stage?.requires_commercial_terms && !hasCommercialTerms(entity)) {
      setPendingMove({ entity, stage, send });
      return;
    }
    // send already toasted and refetched on failure — the rejection only
    // exists so the Tier prompt knows to stay open.
    send({}).catch(() => {});
  };

  const moveEntity = (entity, stageId) => {
    const relationship = prospectRelationship(entity);
    if (!relationship || relationship.relationship_stage_id === stageId) return;
    withCommercialTerms(entity, stageId, (terms) => sendMove(entity, relationship, stageId, terms));
  };

  const sendMove = async (entity, relationship, stageId, terms) => {
    // Optimistic move so the card doesn't snap back while the request is
    // in flight — reverted from a full refetch if the save fails.
    setEntities((prev) => prev.map((e) => (
      e.id === entity.id
        ? { ...e, relationships: e.relationships.map((r) => (r.id === relationship.id ? { ...r, relationship_stage_id: stageId } : r)) }
        : e
    )));
    try {
      await axios.patch(`/api/commercial-entities/${entity.id}/relationships/${relationship.id}/field`, { field: 'relationship_stage_id', value: stageId, ...terms });
      dismissError();
      if (terms.tier_id) fetchBoard();
    } catch (err) {
      showError(err, 'Move failed');
      fetchBoard();
      throw err;
    }
  };

  const handleDrop = (stageId) => {
    setDragOverStageId(null);
    const entity = entities.find((e) => e.id === draggingId);
    setDraggingId(null);
    if (!entity) return;
    moveEntity(entity, stageId);
  };

  // Dropping directly on another card reorders within (or into) that
  // card's Stage, inserting the dragged card just above it — a more
  // precise version of handleDrop's "drop anywhere in the column" append-
  // to-bottom move. Column reorder is per-Stage: sibling cards elsewhere in
  // the board are untouched.
  const reorderEntity = (stageId, beforeEntityId) => {
    setDragOverStageId(null);
    setDragOverCardId(null);
    const entity = entities.find((e) => e.id === draggingId);
    setDraggingId(null);
    if (!entity || entity.id === beforeEntityId) return;
    const relationship = prospectRelationship(entity);
    if (!relationship) return;

    const relationshipIdFor = (e) => (e.id === entity.id ? relationship.id : prospectRelationship(e)?.id);
    const siblings = cardsForStage(stageId).filter((e) => e.id !== entity.id);
    const targetIndex = siblings.findIndex((e) => e.id === beforeEntityId);
    const newOrder = [...siblings];
    newOrder.splice(targetIndex === -1 ? newOrder.length : targetIndex, 0, entity);
    const orderedIds = newOrder.map(relationshipIdFor);
    if (relationship.relationship_stage_id === stageId) {
      sendReorder(entity, relationship, stageId, newOrder, orderedIds, {}).catch(() => {});
    } else {
      withCommercialTerms(entity, stageId, (terms) => sendReorder(entity, relationship, stageId, newOrder, orderedIds, terms));
    }
  };

  const sendReorder = async (entity, relationship, stageId, newOrder, orderedIds, terms) => {
    // Optimistic reorder, same revert-on-failure pattern as moveEntity.
    setEntities((prev) => prev.map((e) => {
      const position = newOrder.findIndex((o) => o.id === e.id);
      if (position === -1) return e;
      const rel = e.id === entity.id ? relationship : prospectRelationship(e);
      if (!rel) return e;
      return {
        ...e,
        relationships: e.relationships.map((r) => (r.id === rel.id
          ? { ...r, relationship_stage_id: stageId, sequence: position }
          : r)),
      };
    }));
    try {
      await axios.patch(`/api/commercial-entities/${entity.id}/relationships/${relationship.id}/reorder`, { stage_id: stageId, ordered_ids: orderedIds, ...terms });
      dismissError();
      if (terms.tier_id) fetchBoard();
    } catch (err) {
      showError(err, 'Reorder failed');
      fetchBoard();
      throw err;
    }
  };

  // Everything inside a card bar its drag wiring — shared by the open Stage
  // columns and the Revisit column.
  const renderCardBody = (entity, { menuItems, dot }) => (
    <>
      {/* Kept out of the card's own click (open the record) and drag. */}
      <div style={{ position: 'absolute', top: 4, right: 4 }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <RowActionsMenu ariaLabel="Card actions" items={menuItems} />
      </div>
      {entity.has_open_task && (
        <CheckSquare size={18} style={{ position: 'absolute', top: 8, right: 34, color: CALENDAR_ITEM_COLORS.task }} title="Has an open Task" />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
        {entity.logo ? (
          <img src={thumbFileUrl(entity.logo.id)} alt="" style={{ display: 'block', flex: 'none', width: 28, height: 28, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, padding: 2 }} />
        ) : (
          // PlaceholderThumb/PersonPlaceholderThumb center themselves
          // via `margin: 0 auto` (built for a table <td>) — inside this
          // flex row that auto-margin gets treated as a flex auto-margin
          // and shoves the dot/name off to the right, so it's boxed in a
          // fixed-size, non-flex wrapper to neutralize that.
          <div style={{ flex: 'none', width: 28, height: 28 }}>
            {entity.type === 'individual' ? <PersonPlaceholderThumb size={28} /> : <PlaceholderThumb size={28} />}
          </div>
        )}
        {dot && <TrafficLightDot state={dot.state} title={dot.title} detail={dot.detail} />}
        <span>{entity.name}</span>
      </div>
      {entity.primary_address && (entity.primary_address.suburb || entity.primary_address.state || entity.primary_address.country) && (
        <div style={{ marginTop: 2, fontSize: 12, color: '#5a6b85' }}>
          {[entity.primary_address.suburb, entity.primary_address.state, entity.primary_address.country].filter(Boolean).join(', ')}
        </div>
      )}
      {mostImportantContact(entity) && (
        <div style={{ marginTop: 2, fontSize: 12, color: '#5a6b85' }} title="Most important contact, by Link Role priority">
          Contact: {mostImportantContact(entity).name}
        </div>
      )}
    </>
  );

  return (
    <div style={{ margin: '32px 24px 48px' }}>
      {closingEntity && (
        <CloseProspectDialog
          entity={closingEntity}
          onCancel={() => setClosingEntity(null)}
          onClosed={() => {
            setClosingEntity(null);
            fetchBoard();
          }}
        />
      )}
      {reopeningEntity && (
        <ReopenProspectDialog
          entity={reopeningEntity}
          onCancel={() => setReopeningEntity(null)}
          onReopened={() => {
            setReopeningEntity(null);
            fetchBoard();
          }}
        />
      )}
      {pendingMove && (
        <CommercialTermsPrompt
          entity={pendingMove.entity}
          stageName={pendingMove.stage.name}
          onCancel={() => setPendingMove(null)}
          onConfirm={async (terms) => {
            await pendingMove.send(terms);
            setPendingMove(null);
          }}
        />
      )}
      {convertingEntity && (
        <ConvertToCustomerDialog
          entity={convertingEntity}
          onCancel={() => setConvertingEntity(null)}
          onConverted={() => {
            setConvertingEntity(null);
            fetchBoard();
          }}
        />
      )}
      <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Pipeline</h2>
      <div style={{ marginTop: 4, fontSize: 13, color: '#5a6b85' }}>
        Prospects by open Stage — drag a card to move it, or click to open the record.
        Customers, and Disqualified or Lost Prospects, are on <a href="/crm-leads" onClick={(e) => { e.preventDefault(); navigate('/crm-leads'); }}>Prospects &amp; Customers</a>.
        A closed Prospect with a revisit date comes back in the Revisit column as that date nears.
      </div>
      <div style={{ marginTop: 12 }}>
        <input
          className="styled-input"
          placeholder="Search name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
        )}
      </div>
      {loading ? (
        <div style={{ padding: 24, color: '#888' }}>Loading...</div>
      ) : stages.length === 0 ? (
        <div style={{ padding: 24, color: '#888' }}>No open Stages configured for the Prospect Relationship Type yet.</div>
      ) : (
        <div style={{ display: 'flex', gap: 14, marginTop: 16, overflowX: 'auto' }}>
          {stages.map((stage) => (
            <div
              key={stage.id}
              style={{ ...columnStyle, background: dragOverStageId === stage.id ? '#e3ecfa' : columnStyle.background }}
              onDragOver={(e) => { e.preventDefault(); setDragOverStageId(stage.id); }}
              onDragLeave={() => setDragOverStageId((cur) => (cur === stage.id ? null : cur))}
              onDrop={(e) => { e.preventDefault(); handleDrop(stage.id); }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 4px' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{stage.name}</span>
                <span style={{ fontSize: 12, color: '#5a6b85' }}>{cardsForStage(stage.id).length}</span>
              </div>
              {/* What the Stage means, from Relationship Types. */}
              {stage.description && (
                <div style={{ fontSize: 12, color: '#5a6b85', padding: '0 4px 6px', lineHeight: 1.4 }}>{stage.description}</div>
              )}
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cardsForStage(stage.id).map((entity) => (
                  <div
                    key={entity.id}
                    draggable
                    onDragStart={() => setDraggingId(entity.id)}
                    onDragEnd={() => { setDraggingId(null); setDragOverCardId(null); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverStageId(stage.id); if (draggingId !== entity.id) setDragOverCardId(entity.id); }}
                    onDragLeave={() => setDragOverCardId((cur) => (cur === entity.id ? null : cur))}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); reorderEntity(stage.id, entity.id); }}
                    onClick={() => navigate(`/commercial-entities/${entity.id}`)}
                    style={{
                      ...cardStyle,
                      opacity: draggingId === entity.id ? 0.4 : 1,
                      borderTop: dragOverCardId === entity.id ? '2px solid #3b6fd6' : cardStyle.border,
                    }}
                    title="Drag to reorder or move to another Stage, or click to open"
                  >
                    {renderCardBody(entity, {
                      menuItems: [
                        { key: 'convert', label: 'Won — Convert to Customer', onClick: () => setConvertingEntity(entity) },
                        { key: 'close', label: 'Close', onClick: () => setClosingEntity(entity) },
                      ],
                      dot: entity.pipeline_dot,
                    })}
                  </div>
                ))}
                {cardsForStage(stage.id).length === 0 && (
                  <div style={{ fontSize: 12, color: '#9aa5b5', padding: '4px 2px' }}>Nothing here</div>
                )}
              </div>
            </div>
          ))}
          {revisitEntities.length > 0 && (
            // Not a drop target, and its cards aren't draggable — a closed
            // Prospect only moves on through Reopen or Close.
            <div style={{ ...columnStyle, background: '#f5f0e6' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 4px' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }} title="Closed Prospects whose revisit date is coming up or due">Revisit</span>
                <span style={{ fontSize: 12, color: '#5a6b85' }}>{revisitCards.length}</span>
              </div>
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {revisitCards.map((entity) => (
                  <div
                    key={entity.id}
                    onClick={() => navigate(`/commercial-entities/${entity.id}`)}
                    style={{ ...cardStyle, cursor: 'pointer' }}
                    title="Click to open"
                  >
                    {renderCardBody(entity, {
                      menuItems: [
                        { key: 'reopen', label: 'Reopen', onClick: () => setReopeningEntity(entity) },
                        { key: 'close', label: 'Close', onClick: () => setClosingEntity(entity) },
                      ],
                      dot: revisitDot(entity),
                    })}
                    <div style={{ marginTop: 2, fontSize: 12, color: '#5a6b85' }}>
                      {prospectRelationship(entity)?.stage?.name}
                      {prospectRelationship(entity)?.close_reason?.name ? ` — ${prospectRelationship(entity).close_reason.name}` : ''}
                    </div>
                  </div>
                ))}
                {revisitCards.length === 0 && (
                  <div style={{ fontSize: 12, color: '#9aa5b5', padding: '4px 2px' }}>Nothing here</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerPipelinePage;
